import { navigateBackOrFallback } from "../../shared/screen-shell/screen-navigation.utils";
import {Component, OnInit} from "@angular/core";
import {Location} from "@angular/common";
import {ActivatedRoute, Router} from "@angular/router";
import {BehaviorSubject, forkJoin, Observable, of} from "rxjs";
import {catchError, finalize} from "rxjs/operators";
import {Chat} from "../../shared/models/chat";
import {Player} from "../../shared/models/player";
import {ChatService} from "../../shared/services/chat.service";
import {DelayedSpinnerService} from "../../shared/services/delayed-spinner.service";
import {PlayerService} from "../../shared/services/player.service";

@Component({
  selector: "chat-add-members",
  templateUrl: "./chat-add-members.component.html",
  styleUrls: ["../chat.component.css"],
  standalone: false
})
export class ChatAddMembersComponent implements OnInit {

  public chat: Chat;
  public currentPlayer: Player;
  public viewedByManager = false;
  public addMemberSource: "footballerFriends" | "openProfiles" = "footballerFriends";
  public addMemberSearchText = "";
  public addMemberCandidates: Player[] = [];
  public selectedAddMembers: Player[] = [];
  public addMemberLoading = false;
  public addMemberSaving = false;
  public addMemberNoResults = false;
  public addMemberError: string;
  public readonly screenReady$ = new BehaviorSubject<boolean>(false);

  constructor(
    private chatService: ChatService,
    private playerService: PlayerService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private spinner: DelayedSpinnerService
  ) {
  }

  ngOnInit(): void {
    this.spinner.show();
    this.screenReady$.next(false);
    this.currentPlayer = this.playerService.getCurrentPlayer();
    const chatId = this.route.snapshot.params["chatId"];
    if (!chatId) {
      this.router.navigate(["/chat-list"]);
      return;
    }
    this.chatService.getChatById(chatId).subscribe(chat => {
      this.chat = chat;
      this.viewedByManager = !!(chat && chat.contact && this.currentPlayer && this.currentPlayer.id === chat.contact.id);
      this.spinner.hide();
      if (!this.canManageChatMembers()) {
        this.router.navigate(["/chat-settings", {chatId: this.chat.id}]);
        return;
      }
      this.screenReady$.next(true);
      this.searchAddMembers();
    }, error => {
      console.log(error);
      this.spinner.hide();
      this.router.navigate(["/chat-list"]);
    });
  }

  public goBack(): void {
    navigateBackOrFallback(this.location, this.router, ["/chat-list"]);
  }

  public setAddMemberSource(source: "footballerFriends" | "openProfiles"): void {
    if (this.addMemberSource === source) {
      return;
    }
    this.addMemberSource = source;
    this.clearAddMemberSearch();
    if (source === "footballerFriends") {
      this.searchAddMembers();
    }
  }

  public searchAddMembers(): void {
    const searchText = (this.addMemberSearchText || "").trim();
    if (this.addMemberSource === "openProfiles" && searchText.length === 0) {
      this.addMemberCandidates = [];
      this.addMemberNoResults = false;
      return;
    }

    this.addMemberLoading = true;
    this.addMemberNoResults = false;
    this.addMemberError = null;
    this.addMemberSearch(searchText).pipe(
      catchError(error => {
        console.log(error);
        this.addMemberError = "chat.addMembersError";
        return of([]);
      }),
      finalize(() => this.addMemberLoading = false)
    ).subscribe(players => {
      this.addMemberCandidates = this.filterAddablePlayers(players || []);
      this.addMemberNoResults = this.addMemberCandidates.length === 0;
    });
  }

  public clearAddMemberSearch(): void {
    this.addMemberSearchText = "";
    this.addMemberCandidates = [];
    this.addMemberNoResults = false;
    this.addMemberError = null;
  }

  public toggleAddMemberCandidate(player: Player): void {
    if (!player || !player.id) {
      return;
    }
    if (this.isSelectedAddMember(player)) {
      this.selectedAddMembers = this.selectedAddMembers.filter(selected => selected.id !== player.id);
      this.searchAddMembers();
      return;
    }
    this.selectedAddMembers.push(player);
    this.addMemberCandidates = this.addMemberCandidates.filter(candidate => candidate.id !== player.id);
  }

  public isSelectedAddMember(player: Player): boolean {
    return !!(player && player.id && this.selectedAddMembers.some(selected => selected.id === player.id));
  }

  public removeSelectedAddMember(player: Player): void {
    if (!player || !player.id) {
      return;
    }
    this.selectedAddMembers = this.selectedAddMembers.filter(selected => selected.id !== player.id);
    this.searchAddMembers();
  }

  public addSelectedMembersToChat(): void {
    if (!this.chat || !this.chat.id || this.selectedAddMembers.length === 0 || this.addMemberSaving) {
      return;
    }

    this.addMemberSaving = true;
    this.addMemberError = null;
    const requests = this.selectedAddMembers.map(player => this.chatService.addChatMember(this.chat.id, player.id));
    forkJoin(requests).pipe(
      finalize(() => this.addMemberSaving = false)
    ).subscribe(() => {
      this.router.navigate(["/chat-settings", {chatId: this.chat.id}]);
    }, error => {
      console.log(error);
      this.addMemberError = "chat.addMembersError";
    });
  }

  private canManageChatMembers(): boolean {
    return !!(this.chat && this.chat.id && (this.viewedByManager || this.canManageChat()));
  }

  private canManageChat(): boolean {
    return !!(this.chat && this.chat.entityType !== "EVENT"
      && ((this.viewedByManager && this.chat.priority >= 10)
        || (this.currentPlayer && this.currentPlayer.superAdmin && this.chat.priority < 10)));
  }

  private addMemberSearch(searchText: string): Observable<Player[]> {
    if (this.addMemberSource === "openProfiles") {
      return this.playerService.getPlayersOpenProfile(searchText);
    }
    return this.playerService.getFootballerFriends(searchText);
  }

  private filterAddablePlayers(players: Player[]): Player[] {
    const existingPlayerIds = new Set((this.chat && this.chat.players ? this.chat.players : [])
      .filter(player => !!player && !!player.id)
      .map(player => player.id));
    const selectedPlayerIds = new Set(this.selectedAddMembers
      .filter(player => !!player && !!player.id)
      .map(player => player.id));
    const uniqueIds = new Set<string>();
    return (players || []).filter(player => {
      if (!player || !player.id || existingPlayerIds.has(player.id) || selectedPlayerIds.has(player.id) || uniqueIds.has(player.id)) {
        return false;
      }
      uniqueIds.add(player.id);
      return true;
    });
  }
}
